import Cookies from "universal-cookie";

const cookies = new Cookies();
const BASE = process.env.DIRECTOR_SERVER_URL || "";

const clientError = ({ status, code, message, retryable = false }) => ({
  status,
  code,
  message,
  retryable,
});

const safeResponseMessage = (status) => {
  if (status === 401) return "Faça login novamente para acessar a Visão Diretoria.";
  if (status === 403) return "Você não tem acesso a esta análise.";
  if (status === 409) return "Os dados foram atualizados. Recarregue e tente novamente.";
  if (status === 422) return "Revise os filtros informados.";
  return "Não foi possível carregar a Visão Diretoria.";
};

const configuredBase = () => {
  if (!BASE) {
    throw clientError({
      status: 0,
      code: "configuration_error",
      message: "Visão Diretoria não configurada.",
    });
  }
  try {
    const base = new URL(BASE);
    if (!["https:", "http:"].includes(base.protocol) || base.username || base.password) {
      throw new Error();
    }
    return base;
  } catch {
    throw clientError({
      status: 0,
      code: "configuration_error",
      message: "Visão Diretoria não configurada.",
    });
  }
};

// Compartilhado por `directorFetch` (lança erro normalizado — usado pelas leituras já existentes)
// e por `directorRawRequest` (nunca lança — usado pelas 2 rotas de escrita novas, §4.8/§4.9): as
// duas fazem a mesma resolução de URL/origem contra `DIRECTOR_SERVER_URL`.
const resolveDirectorUrl = (path) => {
  const base = configuredBase();
  const basePath = base.pathname.replace(/\/+$/, "");
  const url = new URL(`${basePath}/${path}`, `${base.origin}/`);
  if (url.origin !== base.origin) {
    throw clientError({
      status: 0,
      code: "configuration_error",
      message: "Visão Diretoria não configurada.",
    });
  }
  return url;
};

const directorAuthToken = () => {
  const { token } = cookies.get("userId") || {};
  return typeof token === "string" && token ? token : null;
};

const directorFetch = async (path) => {
  const url = resolveDirectorUrl(path);
  const token = directorAuthToken();
  if (!token) {
    throw clientError({
      status: 401,
      code: "authentication_required",
      message: "Faça login novamente para acessar a Visão Diretoria.",
    });
  }
  let response;
  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw clientError({
      status: 0,
      code: "network_error",
      message: "Não foi possível comunicar com a Visão Diretoria.",
      retryable: true,
    });
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw clientError({
      status: response.status,
      code: typeof data?.errorCode === "string" ? data.errorCode : "request_failed",
      message: safeResponseMessage(response.status),
      retryable: data?.retryable === true || response.status >= 500,
    });
  }
  return data;
};

const queryFor = (companyId, filters = {}) => {
  const query = new URLSearchParams({ company_id: String(companyId) });
  const mapping = {
    from: "from",
    to: "to",
    legalEntityId: "legal_entity_id",
    projectKey: "project_key",
    costCenterId: "cost_center_id",
    scenario: "scenario",
  };
  for (const [key, target] of Object.entries(mapping)) {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") query.set(target, String(value));
  }
  return query;
};

const itemVarianceQueryFor = (companyId, filters = {}) => {
  const query = new URLSearchParams({ company_id: String(companyId) });
  const mapping = {
    projectKey: "project_id",
    status: "status",
    cause: "cause",
    page: "page",
    pageSize: "page_size",
  };
  for (const [key, target] of Object.entries(mapping)) {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") query.set(target, String(value));
  }
  return query;
};

const module1QueryFor = (companyId, filters = {}) => {
  const query = new URLSearchParams({ company_id: String(companyId) });
  const mapping = {
    from: "from",
    months: "months",
    scenario: "scenario",
    legalEntityId: "legal_entity_id",
    projectKey: "project_key",
    costCenterId: "cost_center_id",
    scopeType: "scope_type",
    scopeKey: "scope_key",
  };
  for (const [key, target] of Object.entries(mapping)) {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") query.set(target, String(value));
  }
  return query;
};

const MODULE1_SURFACES = new Set([
  "entry",
  "executive-summary",
  "liquidity",
  "cashflow/weekly",
  "critical-periods",
  "causal-explanations",
  "returns",
  "managerial-dre",
  "governance/coverage",
  "governance/portfolio",
]);

const receivablesQueryFor = (companyId, filters = {}) => {
  const query = new URLSearchParams({ company_id: String(companyId) });
  const mapping = {
    from: "from",
    to: "to",
    legalEntityId: "legal_entity_id",
    projectKey: "project_key",
    customerRef: "customer_ref",
    unitRef: "unit_ref",
    basis: "basis",
    page: "page",
    pageSize: "page_size",
  };
  for (const [key, target] of Object.entries(mapping)) {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") query.set(target, String(value));
  }
  return query;
};

// GET /director/readiness e as duas rotas de escrita novas (POST /director/update, GET
// /director/update) usam um transporte separado de `directorFetch`: essas rotas devolvem corpos
// estruturados mesmo em respostas não-ok (403/409/422 têm `code`/`reasons`/`job` que o chamador
// precisa ler), enquanto `directorFetch` normaliza todo erro para uma mensagem genérica segura.
// Consistente com o padrão já usado por `requestUpdateJob`/`loadUpdateJob`
// (`src/contexts/projects/updateJobs.js`): nunca lança, sempre devolve `{ ok, status, body }`.
const directorRawRequest = async (path, { method = "GET", body } = {}) => {
  let url;
  try {
    url = resolveDirectorUrl(path);
  } catch {
    return { ok: false, status: 0, body: null };
  }
  const token = directorAuthToken();
  if (!token) return { ok: false, status: 401, body: null };
  try {
    const response = await fetch(url.toString(), {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => null);
    return { ok: response.ok, status: response.status, body: data };
  } catch {
    return { ok: false, status: 0, body: null };
  }
};

export const fetchDirectorReadiness = (companyId) => (
  directorFetch(`director/readiness?${queryFor(companyId)}`)
);

export const triggerDirectorUpdate = (companyId) => (
  directorRawRequest("director/update", {
    method: "POST",
    body: { company_id: String(companyId) },
  })
);

export const fetchDirectorUpdateJob = (companyId) => (
  directorRawRequest(`director/update?${queryFor(companyId)}`)
);

export const fetchDirectorAccess = (companyId) => (
  directorFetch(`director/access?${queryFor(companyId)}`)
);

export const fetchDirectorMetadata = (companyId) => (
  directorFetch(`director/meta/filters?${queryFor(companyId)}`)
);

export const fetchDirectorCashflow = (companyId, filters = {}) => (
  directorFetch(`director/cashflow?${queryFor(companyId, filters)}`)
);

export const fetchDirectorModule1Surface = (surface, companyId, filters = {}) => {
  if (!MODULE1_SURFACES.has(surface)) {
    return Promise.reject(clientError({
      status: 0,
      code: "configuration_error",
      message: "Superfície Diretoria não configurada.",
    }));
  }
  return directorFetch(`director/${surface}?${module1QueryFor(companyId, filters)}`);
};

export const fetchDirectorTitles = (companyId, filters = {}) => (
  directorFetch(`director/titles?${receivablesQueryFor(companyId, filters)}${
    filters.state ? `&state=${encodeURIComponent(filters.state)}` : ""
  }${filters.direction ? `&direction=${encodeURIComponent(filters.direction)}` : ""}${
    filters.costCenterId ? `&cost_center_id=${encodeURIComponent(filters.costCenterId)}` : ""
  }`)
);

export const fetchDirectorItemVariances = (companyId, filters = {}) => (
  directorFetch(`director/item-variances?${itemVarianceQueryFor(companyId, filters)}`)
);

export const fetchCustomerReceipts = (companyId, filters = {}) => (
  directorFetch(`director/customer-receipts?${receivablesQueryFor(companyId, filters)}`)
);

export const fetchCustomerUnits = (companyId, customerRef, filters = {}) => {
  const safeFilters = { ...filters };
  delete safeFilters.customerRef;
  return directorFetch(
    `director/customers/${encodeURIComponent(customerRef)}/units?${receivablesQueryFor(
      companyId,
      safeFilters,
    )}`,
  );
};

const costPlanQuery = (companyId, projectKey, window = {}) => {
  const query = new URLSearchParams({ company_id: String(companyId), project_key: String(projectKey) });
  for (const key of ['from', 'months', 'offset', 'limit', 'before_revision', 'assignmentAfter', 'job_id']) if (window[key] !== undefined && window[key] !== null) query.set(key, String(window[key]));
  return query;
};
const costPlanPath = id => `director/cost-plans/${encodeURIComponent(id)}`;
export const getCostPlanningSource = (companyId, projectKey) => directorRawRequest(`director/cost-planning/source?${costPlanQuery(companyId, projectKey)}`);
export const previewCostPlanningSource = body => directorRawRequest('director/cost-planning/source/preview', {method:'POST',body});
export const setCostPlanningSource = body => directorRawRequest('director/cost-planning/source', {method:'POST',body});
export const getCostPlan = (companyId, projectKey, window) => directorRawRequest(`director/cost-plans?${costPlanQuery(companyId, projectKey, window)}`);
export const saveCostPlanDraft = (id, body) => directorRawRequest(id ? `${costPlanPath(id)}/draft` : 'director/cost-plans', {method:id ? 'PATCH' : 'POST',body});
export const previewCostPlan = (id, body) => directorRawRequest(`${costPlanPath(id)}/preview`, {method:'POST',body});
export const publishCostPlan = (id, body) => directorRawRequest(`${costPlanPath(id)}/publish`, {method:'POST',body});
export const replanCostPlan = (id, body) => directorRawRequest(`${costPlanPath(id)}/replan`, {method:'POST',body});
export const archiveCostPlan = (id, body) => directorRawRequest(`${costPlanPath(id)}/archive`, {method:'POST',body});
export const updateCostPlanAssignment = (id, factKey, body) => directorRawRequest(`${costPlanPath(id)}/assignments/${encodeURIComponent(factKey)}`, {method:'PATCH',body});
export const getCostPlanMonths = (id, companyId, projectKey, window) => directorRawRequest(`${costPlanPath(id)}/months?${costPlanQuery(companyId, projectKey, window)}`);
export const getCostPlanHistory = (id, companyId, projectKey, window) => directorRawRequest(`${costPlanPath(id)}/history?${costPlanQuery(companyId, projectKey, window)}`);

export default {
  getCostPlanningSource, previewCostPlanningSource, setCostPlanningSource, getCostPlan, saveCostPlanDraft,
  previewCostPlan, publishCostPlan, replanCostPlan, archiveCostPlan, updateCostPlanAssignment, getCostPlanMonths, getCostPlanHistory,
  fetchDirectorAccess,
  fetchDirectorMetadata,
  fetchDirectorCashflow,
  fetchDirectorModule1Surface,
  fetchDirectorTitles,
  fetchDirectorItemVariances,
  fetchCustomerReceipts,
  fetchCustomerUnits,
  fetchDirectorReadiness,
  triggerDirectorUpdate,
  fetchDirectorUpdateJob,
};
